import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { Play, Pause, Volume2, ChevronRight, ChevronLeft } from 'lucide-react'
import homePageApiService, { SectionCard } from '@/services/homePageApiService'
import { getLocalizedValue } from '@sakeena/shared'

// Helper function to normalize card data (handle both camelCase and PascalCase)
const normalizeCard = (card: any): SectionCard => ({
  id: card.id || card.Id || '',
  title: card.title || card.Title || '',
  body: card.body || card.Body || '',
  imageUrl: card.imageUrl || card.ImageUrl,
  audioUrl: card.audioUrl || card.AudioUrl,
  audioDurationSeconds: card.audioDurationSeconds ?? card.AudioDurationSeconds ?? null,
  ctaText: card.ctaText || card.CtaText || '',
  ctaLink: card.ctaLink || card.CtaLink || '',
  order: card.order ?? card.Order ?? 0,
  isActive: card.isActive ?? card.IsActive ?? true,
})

// Helper function to format duration
const formatDuration = (seconds?: number): string => {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function IslamDefinitionPage() {
  const { t, i18n } = useTranslation()
  const safeT = t || ((key: string, def: string) => def || key)
  const currentLang = i18n?.language || 'ar'
  const [searchParams] = useSearchParams()
  const [cards, setCards] = useState<SectionCard[]>([])
  const [activeCard, setActiveCard] = useState<SectionCard | null>(null)
  const [loading, setLoading] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    const loadCards = async () => {
      try {
        setLoading(true)
        const data = await homePageApiService.getActiveSectionCards()
        const normalizedCards = (data || []).map(normalizeCard)
        const sortedCards = normalizedCards
          .filter(card => card.isActive)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
        
        setCards(sortedCards)
        
        // Check if there's a card ID in URL parameters
        const cardIdFromUrl = searchParams.get('card')
        if (cardIdFromUrl && sortedCards.length > 0) {
          // Find the card by ID
          const cardFromUrl = sortedCards.find(c => (c.id || '') === cardIdFromUrl)
          if (cardFromUrl) {
            await loadCardDetails(cardFromUrl.id || '')
          } else {
            // If card not found, load first card
            await loadCardDetails(sortedCards[0].id || '')
          }
        } else if (sortedCards.length > 0) {
          // Load first card details if no card ID in URL
          await loadCardDetails(sortedCards[0].id || '')
        }
      } catch (error) {
        console.error('Failed to load section cards:', error)
      } finally {
        setLoading(false)
      }
    }

    loadCards()
  }, [searchParams, i18n?.language])

  const loadCardDetails = async (cardId: string) => {
    try {
      const data = await homePageApiService.getSectionCardById(cardId)
      if (data) {
        const normalized = normalizeCard(data)
        setActiveCard(normalized)
        
        // Reset audio when switching cards
        if (audioRef.current) {
          audioRef.current.pause()
          audioRef.current = null
          setIsPlaying(false)
        }
      }
    } catch (error) {
      console.error('Failed to load card details:', error)
    }
  }

  const handleCardClick = async (card: SectionCard) => {
    const cardId = card.id || ''
    if (cardId) {
      await loadCardDetails(cardId)
    }
  }

  const handlePlayPause = () => {
    if (!activeCard?.audioUrl) return

    if (!audioRef.current) {
      const audio = new Audio(activeCard.audioUrl)
      audioRef.current = audio

      audio.addEventListener('ended', () => {
        setIsPlaying(false)
      })
    }

    const audio = audioRef.current

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play()
      setIsPlaying(true)
    }
  }

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])


  if (loading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900/40 min-h-screen pt-4 sm:pt-5 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10">
          <div className="text-center text-gray-600 py-20">{safeT('common.loading', 'جاري التحميل...')}</div>
        </div>
      </div>
    )
  }

  const activeCardId = activeCard?.id || ''

  // Find previous and next cards
  const currentIndex = cards.findIndex(card => (card.id || '') === activeCardId)
  const previousCard = currentIndex > 0 ? cards[currentIndex - 1] : null
  const nextCard = currentIndex < cards.length - 1 && currentIndex >= 0 ? cards[currentIndex + 1] : null

  const handlePrevious = () => {
    if (previousCard) {
      handleCardClick(previousCard)
    }
  }

  const handleNext = () => {
    if (nextCard) {
      handleCardClick(nextCard)
    }
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900/40 min-h-screen pt-4 sm:pt-5 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10">
        {/* التابات – Section Cards في الـ subheader */}
        <div className="mb-4 border-b border-gray-200 dark:border-gray-700 pb-3">
          <div className="flex justify-start sm:justify-center text-center gap-x-3 sm:gap-x-4 md:gap-x-6 gap-y-2 text-xs sm:text-sm md:text-base overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {cards.map((card) => {
              const cardId = card.id || ''
              const isActive = cardId === activeCardId
              return (
                <button
                  key={cardId}
                  onClick={() => handleCardClick(card)}
                  className={cn(
                    'relative pb-2 px-2 sm:px-3 transition-colors whitespace-nowrap flex-shrink-0',
                    isActive
                      ? 'text-primary-600 dark:text-primary-400 font-semibold'
                      : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
                  )}
                >
                  {getLocalizedValue(card.title, currentLang)}
                  {isActive && (
                    <span className="absolute left-0 right-0 -bottom-0.5 h-[2px] bg-primary-600 dark:bg-primary-400 rounded-full" />
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* الكارد – عرض المقال مع الصورة والصوت */}
        {activeCard ? (
          <div className="flex items-start sm:items-center gap-1 sm:gap-2 md:gap-4 lg:gap-6 mt-4">
            {/* Previous Article Navigation - Right side (RTL) */}
            {previousCard ? (
              <button
                onClick={handlePrevious}
                className="flex flex-col items-center gap-1 sm:gap-1.5 md:gap-2 group flex-shrink-0 mt-2 sm:mt-0"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 flex items-center justify-center bg-white dark:bg-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all group-hover:scale-110 text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7" />
                </div>
                <div className="text-center max-w-[80px] sm:max-w-[100px] md:max-w-[120px] lg:max-w-[150px] hidden sm:block">
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-0.5 md:mb-1">{safeT('common.previous', 'السابق')}</p>
                  <p className="text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {getLocalizedValue(previousCard.title, currentLang)}
                  </p>
                </div>
              </button>
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 flex-shrink-0" />
            )}

            {/* Article Card */}
            <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl md:rounded-[28px] shadow-[0_18px_60px_rgba(15,23,42,0.08)] overflow-hidden min-w-0">
              <div className="px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-4 sm:py-6 md:py-8 lg:py-10">
              {/* If no image, use single column layout; otherwise use 2 columns */}
              {activeCard.imageUrl ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-10 items-start">
                  {/* النص على اليمين */}
                  <div className="order-2 md:order-1 space-y-2 sm:space-y-3 md:space-y-4">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {safeT('islamDefinition.subtitle', 'تعريف بالإسلام – من قلب المسلم')}
                    </p>

                    <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-extrabold text-gray-900 dark:text-gray-100 leading-tight">
                      {getLocalizedValue(activeCard.title, currentLang)}
                    </h2>

                    <div
                      className="prose prose-sm sm:prose-base md:prose-lg max-w-none text-gray-800 dark:text-gray-200 leading-relaxed sm:leading-loose text-sm sm:text-base md:text-lg lg:text-xl font-medium space-y-3 sm:space-y-4"
                      dangerouslySetInnerHTML={{
                        __html: getLocalizedValue(activeCard.body, currentLang) || '',
                      }}
                    />
                  </div>

                  {/* الصوت + الصورة على الشمال */}
                  <div className="order-1 md:order-2 space-y-3 sm:space-y-4 md:space-y-5">
                    {/* Audio Player - Compact size */}
                    {activeCard.audioUrl && (
                      <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2 sm:p-2.5 shadow-sm">
                        <button
                          onClick={handlePlayPause}
                          className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-slate-900 dark:bg-slate-800 text-white shadow hover:bg-slate-800 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
                        >
                          {isPlaying ? (
                            <Pause className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          ) : (
                            <Play className="w-3 h-3 sm:w-3.5 sm:h-3.5 ml-0.5" />
                          )}
                        </button>

                        <div className="flex-1 flex items-center gap-2 min-w-0">
                          <div className="hidden sm:flex items-end gap-[1px] h-4 text-emerald-500 flex-1">
                            {[4, 6, 8, 5, 9, 7, 4, 8, 5, 7, 9, 6, 4].map((h, idx) => (
                              <span
                                key={idx}
                                className={`w-[1.5px] rounded-full transition-all ${
                                  isPlaying ? 'bg-emerald-500 animate-pulse' : 'bg-emerald-300'
                                }`}
                                style={{ height: `${h}px` }}
                              />
                            ))}
                          </div>

                          <span className="text-xs font-medium text-slate-600 dark:text-slate-400 min-w-[2.5rem] text-left whitespace-nowrap">
                            {formatDuration(activeCard.audioDurationSeconds)}
                          </span>

                          <button className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors flex-shrink-0">
                            <Volume2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* صورة إضافية إذا كانت موجودة */}
                    <div className="relative">
                      <div className="h-40 sm:h-48 md:h-56 lg:h-64 xl:h-72 w-full rounded-xl sm:rounded-2xl md:rounded-3xl overflow-hidden shadow-lg">
                        <img
                          src={activeCard.imageUrl}
                          alt={getLocalizedValue(activeCard.title, currentLang)}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Single column layout when no image - content takes full width */
                <div className="space-y-3 sm:space-y-4 md:space-y-5 max-w-4xl mx-auto">
                  {/* Audio Player - Compact size at top */}
                  {activeCard.audioUrl && (
                    <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2 sm:p-2.5 shadow-sm w-fit">
                      <button
                        onClick={handlePlayPause}
                        className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-slate-900 dark:bg-slate-800 text-white shadow hover:bg-slate-800 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
                      >
                        {isPlaying ? (
                          <Pause className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        ) : (
                          <Play className="w-3 h-3 sm:w-3.5 sm:h-3.5 ml-0.5" />
                        )}
                      </button>

                      <div className="flex items-center gap-2 min-w-0">
                        <div className="hidden sm:flex items-end gap-[1px] h-4 text-emerald-500">
                          {[4, 6, 8, 5, 9, 7, 4, 8, 5, 7, 9, 6, 4].map((h, idx) => (
                            <span
                              key={idx}
                              className={`w-[1.5px] rounded-full transition-all ${
                                isPlaying ? 'bg-emerald-500 animate-pulse' : 'bg-emerald-300'
                              }`}
                              style={{ height: `${h}px` }}
                            />
                          ))}
                        </div>

                        <span className="text-xs font-medium text-slate-600 dark:text-slate-400 min-w-[2.5rem] text-left whitespace-nowrap">
                          {formatDuration(activeCard.audioDurationSeconds)}
                        </span>

                        <button className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors flex-shrink-0">
                          <Volume2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Content takes full width */}
                  <div className="space-y-2 sm:space-y-3 md:space-y-4">
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {safeT('islamDefinition.subtitle', 'تعريف بالإسلام – من قلب المسلم')}
                    </p>

                    <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-extrabold text-gray-900 dark:text-gray-100 leading-tight">
                      {getLocalizedValue(activeCard.title, currentLang)}
                    </h2>

                    <div
                      className="prose prose-sm sm:prose-base md:prose-lg max-w-none text-gray-800 dark:text-gray-200 leading-relaxed sm:leading-loose text-sm sm:text-base md:text-lg lg:text-xl font-medium space-y-3 sm:space-y-4"
                      dangerouslySetInnerHTML={{
                        __html: getLocalizedValue(activeCard.body, currentLang) || '',
                      }}
                    />
                  </div>
                </div>
              )}
              </div>
            </div>

            {/* Next Article Navigation - Left side (RTL) */}
            {nextCard ? (
              <button
                onClick={handleNext}
                className="flex flex-col items-center gap-1 sm:gap-1.5 md:gap-2 group flex-shrink-0 mt-2 sm:mt-0"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 flex items-center justify-center bg-white dark:bg-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all group-hover:scale-110 text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7" />
                </div>
                <div className="text-center max-w-[80px] sm:max-w-[100px] md:max-w-[120px] lg:max-w-[150px] hidden sm:block">
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-0.5 md:mb-1">{safeT('common.next', 'التالي')}</p>
                  <p className="text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {getLocalizedValue(nextCard.title, currentLang)}
                  </p>
                </div>
              </button>
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 flex-shrink-0" />
            )}
          </div>
        ) : (
          <div className="bg-white rounded-[28px] shadow-[0_18px_60px_rgba(15,23,42,0.08)] px-4 sm:px-8 lg:px-12 py-8 md:py-10 mt-4">
            <div className="text-center text-gray-500 py-10">
              {safeT('public.library.chooseTopic', 'اختر موضوعاً لعرضه')}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
